import { DirectUpload } from '@rails/activestorage'

export class FileUploader {
  constructor(file, url) {
    this.file = file
    this.url = url
    this.uploadController = null
    this.timeoutId = null
    this.aborted = false
    this.callbacks = {}
    this.UPLOAD_TIMEOUT = 120000 // 2 minutes timeout
  }

  // Upload method now takes callbacks instead of emitting events
  upload = (callbacks = {}) => {
    return new Promise((resolve, reject) => {
      this.callbacks = {
        onProgress: callbacks.onProgress || (() => {}),
        onComplete: callbacks.onComplete || (() => {}),
        onError: callbacks.onError || (() => {}),
      }

      this.file.status = 'uploading'
      this.file.progress = 0
      this.lastProgressTime = Date.now()

      const upload = new DirectUpload(this.file, this.url, this)
      this.uploadController = upload

      // Set a timeout to detect stalled uploads
      this.timeoutId = setTimeout(() => this.handleUploadTimeout(), this.UPLOAD_TIMEOUT)

      upload.create((error, blob) => {
        clearTimeout(this.timeoutId)
        this.cleanup()

        if (error) {
          console.error('Upload error:', error)
          this.file.status = 'error'
          this.callbacks.onError(error)
          reject(error)
        }
        else if (this.aborted) {
          this.file.status = 'aborted'
          const abortError = new Error('Upload aborted')
          this.callbacks.onError(abortError)
          reject(abortError)
        }
        else {
          this.file.status = 'complete'
          this.file.signedId = blob.signed_id
          this.callbacks.onComplete(blob)
          resolve(blob)
        }
      })

      // Add event listener to handle page unload
      window.addEventListener('beforeunload', this.handleBeforeUnload)
    })
  }

  handleUploadTimeout = () => {
    // Check if the upload is stalled (no progress for 2 minutes)
    const now = Date.now()
    if (now - this.lastProgressTime > this.UPLOAD_TIMEOUT) {
      this.abort('Upload timed out')
    }
  }

  abort = (reason) => {
    if (this.uploadController && this.file.status === 'uploading') {
      this.aborted = true
      this.file.status = 'aborted'

      try {
        // Attempt to abort the XHR request if possible
        if (this.uploadController.xhr) {
          this.uploadController.xhr.abort()
        }
      }
      catch (e) {
        console.error('Error aborting upload:', e)
      }

      const error = new Error(reason || 'Upload aborted')
      this.callbacks.onError(error)
      return true
    }
    return false
  }

  handleBeforeUnload = (event) => {
    // Only warn if upload is in progress
    if (this.file.status === 'uploading') {
      // Standard way to show a browser warning when navigating away
      event.preventDefault()
      event.returnValue = 'Upload in progress. Are you sure you want to leave?'
      return event.returnValue
    }
  }

  cleanup = () => {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    clearTimeout(this.timeoutId)
  }

  handleProgress = (event) => {
    this.lastProgressTime = Date.now() // Update last progress time
    const progress = parseFloat(event.loaded / event.total) * 100
    this.file.progress = progress

    // Call the progress callback instead of emitting event
    this.callbacks.onProgress(progress)
  }

  // https://edgeguides.rubyonrails.org/active_storage_overview.html#integrating-with-libraries-or-frameworks
  directUploadWillStoreFileWithXHR = (request) => {
    this.xhr = request // Store XHR reference for potential abortion

    request.upload.addEventListener('progress', event => this.handleProgress(event))

    // Add event listeners for network errors
    request.addEventListener('error', () => {
      const error = new Error('Network error during upload')
      this.callbacks.onError(error)
    })

    request.addEventListener('abort', () => {
      const error = new Error('Upload aborted')
      this.callbacks.onError(error)
    })

    request.addEventListener('timeout', () => {
      const error = new Error('Upload timed out')
      this.callbacks.onError(error)
    })
  }
}
